/**
 * Page Transition Loader
 * Shows a skeleton overlay (matching the destination page) when navigating
 * between pages.  Replaces the old white-spinner overlay with contextual
 * skeleton placeholders so the transition feels seamless.
 * Safari / iOS compatible.
 */

(function () {
  'use strict';

  var loader = null;
  var showTimeout = null;
  var safetyTimeout = null;
  var isActive = false;
  var pendingEvent = null;
  var pendingLink = null;

  /* ── Skeleton builders ─────────────────────────────────────────── */

  function detectPageType(href) {
    try {
      var path = new URL(href, location.origin).pathname;
      if (/\/products\/[^\/]/.test(path)) return 'product';
      if (/\/collections\//.test(path)) return 'collection';
      if (path === '/' || path === '') return 'index';
    } catch (e) {}
    return 'generic';
  }

  function repeat(n, fn) {
    var s = '';
    for (var i = 0; i < n; i++) s += fn();
    return s;
  }

  function skeletonProduct() {
    return (
      '<div class="pdp-skeleton-loader__chrome">' +
        '<div class="pdp-skeleton-loader__topbar">' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__logo"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__search"></div>' +
          '<div class="pdp-skeleton-loader__actions">' + repeat(3, function () { return '<div class="skeleton-loader__item pdp-skeleton-loader__action"></div>'; }) + '</div>' +
        '</div>' +
        '<div class="pdp-skeleton-loader__nav">' + repeat(7, function () { return '<div class="skeleton-loader__item pdp-skeleton-loader__nav-item"></div>'; }) + '</div>' +
      '</div>' +
      '<div class="pdp-skeleton-loader__container">' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__breadcrumb"></div>' +
        '<div class="pdp-skeleton-loader__grid">' +
          skeletonProductIntro('mobile') +
          '<div class="pdp-skeleton-loader__media">' +
            '<div class="skeleton-loader__item pdp-skeleton-loader__media-main"></div>' +
            '<div class="skeleton-loader__item pdp-skeleton-loader__dots"></div>' +
            '<div class="pdp-skeleton-loader__thumbs">' + repeat(4, function () { return '<div class="skeleton-loader__item pdp-skeleton-loader__thumb"></div>'; }) + '</div>' +
            '<div class="skeleton-loader__item pdp-skeleton-loader__media-tabs"></div>' +
            '<div class="pdp-skeleton-loader__feature-grid">' + repeat(3, function () { return '<div class="skeleton-loader__item pdp-skeleton-loader__feature"></div>'; }) + '</div>' +
          '</div>' +
          '<div class="pdp-skeleton-loader__info">' +
            skeletonProductIntro('desktop') +
            skeletonProductDetails() +
          '</div>' +
        '</div>' +
      '</div>' +
      skeletonProductSticky()
    );
  }

  function skeletonChrome() {
    return (
      '<div class="pdp-skeleton-loader__chrome" aria-hidden="true">' +
        '<div class="pdp-skeleton-loader__topbar">' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__logo"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__search"></div>' +
          '<div class="pdp-skeleton-loader__actions">' + repeat(3, function () { return '<div class="skeleton-loader__item pdp-skeleton-loader__action"></div>'; }) + '</div>' +
        '</div>' +
        '<div class="pdp-skeleton-loader__nav">' + repeat(7, function () { return '<div class="skeleton-loader__item pdp-skeleton-loader__nav-item"></div>'; }) + '</div>' +
      '</div>'
    );
  }

  function skeletonProductIntro(mode) {
    return (
      '<div class="pdp-skeleton-loader__intro pdp-skeleton-loader__intro--' + mode + '">' +
        '<div class="pdp-skeleton-loader__meta">' + repeat(3, function () { return '<div class="skeleton-loader__item pdp-skeleton-loader__meta-item"></div>'; }) + '</div>' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__title-line pdp-skeleton-loader__title-line--long"></div>' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__title-line"></div>' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__title-line pdp-skeleton-loader__title-line--short"></div>' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__rating"></div>' +
      '</div>'
    );
  }

  function skeletonProductDetails() {
    return (
      '<div class="pdp-skeleton-loader__details">' +
        '<div class="pdp-skeleton-loader__options">' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__delivery-strip"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__label"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__variant-block"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__label pdp-skeleton-loader__label--short"></div>' +
          '<div class="pdp-skeleton-loader__swatches">' + repeat(4, function () { return '<div class="skeleton-loader__item pdp-skeleton-loader__swatch"></div>'; }) + '</div>' +
        '</div>' +
        '<div class="pdp-skeleton-loader__purchase">' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__price-card"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__delivery-card"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__cta"></div>' +
        '</div>' +
      '</div>'
    );
  }

  function skeletonProductSticky() {
    return (
      '<div class="pdp-skeleton-loader__mobile-sticky">' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__sticky-image"></div>' +
        '<div class="pdp-skeleton-loader__sticky-copy"><div class="skeleton-loader__item pdp-skeleton-loader__sticky-line"></div><div class="skeleton-loader__item pdp-skeleton-loader__sticky-price"></div></div>' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__sticky-cta"></div>' +
      '</div>'
    );
  }

  function skeletonCollection() {
    return (
      skeletonChrome() +
      '<div class="collection-skeleton-loader__container">' +
        '<div class="collection-skeleton-loader__toolbar">' +
          '<div class="skeleton-loader__item skeleton-loader__item--title"></div>' +
          '<div class="skeleton-loader__item collection-skeleton-loader__toolbar-sort"></div>' +
        '</div>' +
        '<div class="skeleton-loader__grid skeleton-loader__grid--products">' +
          repeat(8, function () {
            return (
              '<div class="skeleton-loader__card">' +
                '<div class="skeleton-loader__item skeleton-loader__item--image"></div>' +
                '<div class="skeleton-loader__item skeleton-loader__item--text skeleton-loader__item--medium"></div>' +
                '<div class="skeleton-loader__item skeleton-loader__item--text skeleton-loader__item--short"></div>' +
              '</div>'
            );
          }) +
        '</div>' +
      '</div>'
    );
  }

  function skeletonIndex() {
    return (
      skeletonChrome() +
      '<div class="skeleton-loader__container">' +
        '<div class="skeleton-loader__section">' +
          '<div class="skeleton-loader__header"><div class="skeleton-loader__item skeleton-loader__item--title"></div></div>' +
          '<div class="skeleton-loader__grid skeleton-loader__grid--categories">' +
            repeat(6, function () { return '<div class="skeleton-loader__item skeleton-loader__item--circle"></div>'; }) +
          '</div>' +
        '</div>' +
        '<div class="skeleton-loader__hero">' +
          '<div class="skeleton-loader__item skeleton-loader__item--hero"></div>' +
        '</div>' +
        '<div class="skeleton-loader__section">' +
          '<div class="skeleton-loader__header"><div class="skeleton-loader__item skeleton-loader__item--title"></div></div>' +
          '<div class="skeleton-loader__grid skeleton-loader__grid--products">' +
            repeat(4, function () {
              return (
                '<div class="skeleton-loader__card">' +
                  '<div class="skeleton-loader__item skeleton-loader__item--image"></div>' +
                  '<div class="skeleton-loader__item skeleton-loader__item--text skeleton-loader__item--medium"></div>' +
                  '<div class="skeleton-loader__item skeleton-loader__item--text skeleton-loader__item--short"></div>' +
                '</div>'
              );
            }) +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function skeletonGeneric() {
    return (
      skeletonChrome() +
      '<div class="skeleton-loader__container">' +
        '<div class="skeleton-loader__section">' +
          '<div class="skeleton-loader__header"><div class="skeleton-loader__item skeleton-loader__item--title"></div></div>' +
          '<div class="skeleton-loader__item skeleton-loader__item--text skeleton-loader__item--medium" style="margin-bottom:16px"></div>' +
          '<div class="skeleton-loader__item skeleton-loader__item--text skeleton-loader__item--short" style="margin-bottom:24px"></div>' +
          '<div class="skeleton-loader__item" style="width:100%;height:200px;border-radius:12px"></div>' +
        '</div>' +
      '</div>'
    );
  }

  var builders = {
    product: skeletonProduct,
    collection: skeletonCollection,
    index: skeletonIndex,
    generic: skeletonGeneric
  };

  /* ── Loader lifecycle ──────────────────────────────────────────── */

  function createLoader() {
    var el = document.createElement('div');
    var isArabic = (document.documentElement.lang || '').toLowerCase().indexOf('ar') === 0;
    el.className = 'page-loader';
    el.id = 'pageLoader';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    el.setAttribute('aria-label', isArabic ? 'جارٍ تحميل الصفحة' : 'Loading page');
    document.body.appendChild(el);
    return el;
  }

  function show() {
    if (!loader || isActive) return;
    isActive = true;

    var type = 'generic';
    if (pendingLink && pendingLink.href) {
      type = detectPageType(pendingLink.href);
    }

    loader.dataset.pageType = type;
    loader.innerHTML = (builders[type] || builders.generic)();
    loader.classList.add('active');
    document.body.classList.add('page-loader-lock');
    document.body.setAttribute('aria-busy', 'true');

    // Keep the destination skeleton visible until the browser commits the new
    // document. Collection responses can legitimately take longer than 2.5s;
    // the old timeout exposed the source page again just before navigation.
    // This remains a safety net for a genuinely failed/cancelled navigation.
    safetyTimeout = setTimeout(hide, 10000);
  }

  function hide() {
    clearTimeout(showTimeout);
    clearTimeout(safetyTimeout);
    showTimeout = null;
    safetyTimeout = null;
    pendingEvent = null;
    pendingLink = null;

    if (!loader) return;

    isActive = false;
    loader.classList.remove('active');
    document.body.classList.remove('page-loader-lock');
    document.body.removeAttribute('aria-busy');
  }

  /* ── Event helpers ─────────────────────────────────────────────── */

  // Safari sometimes returns a Text node as target
  function getEventElementTarget(e) {
    var t = e.target;
    if (!t) return null;
    if (t.nodeType === 3) t = t.parentElement;
    return t;
  }

  function shouldShowLoader(link) {
    if (!link || !link.href) return false;

    var href = link.getAttribute('href');
    var destination;

    // Skip non-navigation links
    if (!href || href === '#' || href === '' || href.charAt(0) === '#') return false;
    if (href.indexOf('javascript:') === 0) return false;
    if (link.target === '_blank') return false;
    if (link.hasAttribute('download')) return false;
    try {
      destination = new URL(link.href, window.location.href);
    } catch (error) {
      return false;
    }
    if (destination.protocol !== 'http:' && destination.protocol !== 'https:') return false;
    if (destination.origin !== window.location.origin) return false;

    // Skip exact-current URLs and same-document fragment navigation. A link
    // written as `/products/foo#reviews` is still an in-page jump and must not
    // leave the overlay locked while only the hash changes.
    if (destination.href === window.location.href) return false;
    if (
      destination.pathname === window.location.pathname &&
      destination.search === window.location.search &&
      destination.hash
    ) return false;

    // Skip only controls that explicitly own an in-page interaction. Broad
    // container exclusions such as `.drawer`, `details` and `.accordion`
    // suppressed legitimate full-page links in navigation menus and panels.
    // Intercepted actions still get a chance to call preventDefault before
    // the zero-delay show task runs below.
    var skipSelectors = [
      '[data-prevent-loader]', '[data-modal]', '[data-modal-trigger]', '[data-ajax]',
      /* In-page AJAX swap for custom.extra_options links - owned by
         assets/linked-options-ajax.js. Skip the page transition so it
         doesn't compete with the section swap. */
      '.lv-option-item-link'
    ];

    for (var i = 0; i < skipSelectors.length; i++) {
      try {
        if (link.closest && link.closest(skipSelectors[i])) return false;
      } catch (e) {}
    }

    return true;
  }

  function warmSkeletonStyles() {
    if (typeof window.__loadTransitionSkeletonStyles === 'function') {
      window.__loadTransitionSkeletonStyles();
    }
  }

  function handleNavigationIntent(e) {
    var target = getEventElementTarget(e);
    var link = target && target.closest ? target.closest('a') : null;
    if (shouldShowLoader(link)) warmSkeletonStyles();
  }

  function handleClick(e) {
    // Only left click / primary tap
    if (e.button && e.button !== 0) return;

    // Skip if modifier keys (new tab etc.)
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

    // If something already prevented it, don't show loader
    if (e.defaultPrevented) return;

    var target = getEventElementTarget(e);
    if (!target) return;

    var link = target.closest ? target.closest('a') : null;
    if (!link) return;

    if (shouldShowLoader(link)) {
      warmSkeletonStyles();
      clearTimeout(showTimeout);
      pendingEvent = e;
      pendingLink = link;

      // Let the current event finish so a drag/AJAX handler can still call
      // preventDefault, then paint the transition on the next task. This keeps
      // navigation native and adds no artificial delay.
      showTimeout = setTimeout(function () {
        // Check again if event was prevented during bubble
        if (pendingEvent && pendingEvent.defaultPrevented) {
          pendingEvent = null;
          pendingLink = null;
          return;
        }
        pendingEvent = null;
        show();
      }, 0);
    }
  }

  function handleSubmit(e) {
    var form = e.target;

    // If already prevented, skip
    if (e.defaultPrevented) return;

    // Skip AJAX forms
    if (form.hasAttribute('data-ajax')) return;
    if (form.closest && form.closest('product-form')) return;
    if (form.getAttribute('data-type') === 'add-to-cart-form') return;
    if (form.action && form.action.indexOf('/cart/add') !== -1) return;
    if (form.closest && form.closest('predictive-search')) return;

    warmSkeletonStyles();
    clearTimeout(showTimeout);
    pendingLink = null;
    showTimeout = setTimeout(function () {
      if (e.defaultPrevented) return;
      show();
    }, 150);
  }

  function init() {
    loader = createLoader();

    // Use bubble phase (false) so other handlers can preventDefault first
    document.addEventListener('click', handleClick, false);
    document.addEventListener('submit', handleSubmit, false);
    ['pointerover', 'touchstart', 'focusin', 'mousedown'].forEach(function (eventName) {
      document.addEventListener(eventName, handleNavigationIntent, { capture: true, passive: true });
    });

    // Hide as soon as document is interactive
    function hideWhenReady() {
      if (document.readyState === 'interactive' || document.readyState === 'complete') hide();
    }
    document.addEventListener('readystatechange', hideWhenReady, false);
    hideWhenReady();

    window.addEventListener('load', hide, false);
    window.addEventListener('pageshow', hide, false);
    window.addEventListener('pagehide', hide, false);
    window.addEventListener('popstate', hide, false);

    // Hide if page becomes visible (tab switch back)
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') hide();
    }, false);
  }

  // Expose globally
  window.PageLoader = { show: show, hide: hide };

  // Init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, false);
  } else {
    init();
  }
})();
